import { useCallback, useState } from 'react'
import { Outlet } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add as AddIcon } from '@factorialco/f0-react/icons/app'
import {
  type ExternalAccessPageItemFragment,
  ExternalAccessesPageQueryDocument,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import BecomeModal from 'modules/authentication/components/BecomeModal'
import DeactivateExternalAccessModal from 'modules/external_users/components/DeactivateExternalAccessModal'
import EditUserModal from 'modules/external_users/components/EditUserModal'
import { useInviteAccess } from 'modules/external_users/hooks/useInviteAccess'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const Externals = () => {
  const [isNewUserModalOpen, setIsNewUserModalOpen] = useState(false)
  const [editingAccess, setEditingAccess] = useState<ExternalAccessPageItemFragment | null>(null)
  const [deactivatingAccess, setDeactivatingAccess] =
    useState<ExternalAccessPageItemFragment | null>(null)
  const [enableBecomeModal, setBecomeMode] = useState<ExternalAccessPageItemFragment | null>(null)

  const { data: permissionsData, client } = useQuery(ExternalAccessesPageQueryDocument, {
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: false,
  })
  const canCreate = permissionsData?.authentication?._policies?.accessCreateExternal?.can ?? false

  const { invite } = useInviteAccess({
    onCompleted(data) {
      if (data.apiCoreMutations.inviteApicoreAccess?.errors.length) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('settings.external_users.actions.resend_invitation.error'),
        })
      } else {
        app.setFlash({
          type: 'correct',
          message: i18n.t('settings.external_users.actions.resend_invitation.success'),
        })
      }
    },
  })

  const source = useGraphqlDataCollectionSource({
    document: ExternalAccessesPageQueryDocument,
    fetchPolicy: 'cache-and-network',
    mapVariables: () => ({}),
    mapPagination: (data) => {
      const connection = data?.apiCore?.accessesConnection
      return {
        total: connection?.totalCount ?? 0,
        cursor: connection?.pageInfo?.endCursor ?? undefined,
        hasNextPage: connection?.pageInfo?.hasNextPage ?? false,
      }
    },
    mapData: (data) => {
      return data?.apiCore?.accessesConnection?.nodes ?? []
    },
    sortings: {
      firstName: {
        label: i18n.t('employee_form.first_name'),
      },
      lastName: {
        label: i18n.t('employee_form.last_name'),
      },
      email: {
        label: i18n.t('common.email'),
      },
    },
    defaultSorting: {
      field: 'firstName',
      order: 'asc',
    },
    paginationType: 'infinite-scroll',
    primaryActions: () =>
      canCreate
        ? {
            label: i18n.t('settings.external_users.actions.new_user.title'),
            icon: AddIcon,
            onClick: () => setIsNewUserModalOpen(true),
          }
        : undefined,
    itemActions: (access) => {
      const actions = []
      const canInvite = access._policies?.invite.can ?? false
      const canUpdate = access._policies?.update.can ?? false
      const canBecome = access._policies?.become.can ?? false

      if (canInvite && !access.isConfirmed) {
        actions.push({
          label: i18n.t('settings.external_users.actions.resend_invitation.title'),
          onClick: async () => {
            app.setProgress({
              message: i18n.t('common.saving'),
            })
            await invite({
              id: access.id,
            })
            app.setProgress(null)
          },
        })
      }

      if (canUpdate) {
        actions.push({
          label: i18n.t('settings.permissions.users.edit'),
          onClick: () => setEditingAccess(access),
        })
      }

      if (canBecome) {
        actions.push({
          label: i18n.t('settings.external_users.actions.become.title'),
          onClick: () => setBecomeMode(access),
        })
      }

      if (!access.isDeactivated) {
        actions.push({
          label: i18n.t('settings.external_users.actions.deactivate_user.title'),
          onClick: () => setDeactivatingAccess(access),
        })
      }

      return actions
    },
  })

  const getStatusLabelText = useCallback((user: ExternalAccessPageItemFragment) => {
    if (user.isDeactivated) {
      return i18n.t('settings.external_users.status.inactive')
    }
    if (user.isConfirmed) {
      return i18n.t('settings.external_users.status.confirmed')
    }
    return i18n.t('settings.external_users.status.invited')
  }, [])

  const renderList = () => {
    return (
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'list',
            options: {
              fields: [
                {
                  label: i18n.t('common.status'),
                  render: (item: ExternalAccessPageItemFragment) => {
                    const labelText = getStatusLabelText(item)
                    return {
                      type: 'tag',
                      value: {
                        label: labelText,
                      },
                    }
                  },
                },
              ],
              itemDefinition: (item: ExternalAccessPageItemFragment) => {
                return {
                  title: item.fullName,
                  description: [
                    item.affiliation === 'partner'
                      ? i18n.t('common.roles.partner')
                      : i18n.t('common.roles.external'),
                    item.email ?? '',
                  ],
                  avatar: {
                    type: 'person',
                    firstName: item.firstName,
                    lastName: item.lastName,
                    src: item.avatar ?? undefined,
                  },
                }
              },
            },
          },
        ]}
      />
    )
  }

  return (
    <ResponsivePage.Body variant="narrow">
      {renderList()}

      {isNewUserModalOpen && (
        <EditUserModal
          onClose={() => {
            setIsNewUserModalOpen(false)
          }}
          onSuccess={() => {
            client?.refetchQueries({ include: [ExternalAccessesPageQueryDocument] })
          }}
        />
      )}

      {editingAccess && (
        <EditUserModal
          onClose={() => {
            setEditingAccess(null)
          }}
          onSuccess={() => {
            client?.refetchQueries({ include: [ExternalAccessesPageQueryDocument] })
          }}
          userId={editingAccess.id}
        />
      )}

      {deactivatingAccess && (
        <DeactivateExternalAccessModal
          access={deactivatingAccess}
          onClose={() => {
            setDeactivatingAccess(null)
          }}
        />
      )}

      {enableBecomeModal && (
        <BecomeModal
          onClose={() => {
            setBecomeMode(null)
          }}
          accessId={enableBecomeModal.id}
          firstName={enableBecomeModal.firstName}
        />
      )}
      <Outlet />
    </ResponsivePage.Body>
  )
}

export default Externals
